import React, { useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { Box, Typography, Button, Autocomplete, TextField } from "@mui/material";
import bgBlock from "../assets/joma/bg-block.png";

// Global styles to override Google Maps default InfoWindow padding and close button
const globalMapStyles = `
  /* Remove all padding and set border radius for the main InfoWindow container */
  .gm-style-iw {
    padding: 0 !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }
  
  /* Target the exact internal container Google uses to wrap content */
  .gm-style-iw-c {
    padding: 0 !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }
  
  /* Target the content wrapper inside the InfoWindow */
  .gm-style-iw-d {
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 12px !important;
    /* Ensure no scrollbars */
    max-height: none !important;
  }
  
  /* Remove any nested padding/margin that might be pushing the image down */
  .gm-style .gm-style-iw-c .gm-style-iw-d {
    overflow: hidden !important;
    padding: 0 !important;
  }
  
  .gm-style-iw-d > div {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Force the close button wrapper to absolute positioning so it doesn't affect document flow */
  button.gm-ui-hover-effect {
    position: absolute !important;
    top: 6px !important;
    right: 6px !important;
    background-color: rgba(0, 0, 0, 0.4) !important;
    border-radius: 50% !important;
    width: 24px !important;
    height: 24px !important;
    z-index: 10 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
  }
  
  /* Style the actual X icon inside the close button */
  button.gm-ui-hover-effect span {
    width: 14px !important;
    height: 14px !important;
    margin: 5px !important;
    background-color: white !important;
  }
  
  /* Hide the default white background block for the close button if Google injects it */
  .gm-style-iw-tc::after {
    /* Keeps the little pointer arrow pointing to the pin */
  }
`;

const center = {
  lat: 25.7013,
  lng: -100.3888,
};

export const jomarStores = [
  { id: 1, name: "Suc. Penitenciaria", address: "Jomar Autopartes Suc. Penitenciaria, Monterrey", lat: 25.7234883, lng: -100.3381892, hours: "L-S: 8:00 AM - 9:00 PM | D: 8:00 AM - 6:00 PM" },
  { id: 2, name: "Suc. Santa Catarina", address: "Jomar Autopartes Suc. Santa Catarina", lat: 25.6790888, lng: -100.4393758, hours: "L-S: 8:00 AM - 9:00 PM | D: 8:00 AM - 6:00 PM" },
];

const MapComponent = () => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    // Replace with your actual Google Maps API Key
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = React.useState(null);
  const [selectedStore, setSelectedStore] = useState(null);

  const onLoad = React.useCallback(function callback(map) {
    // You could adjust bounds here if needed
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  if (!isLoaded)
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          bgcolor: "#f0f0f0",
        }}
      >
        Cargando mapa...
      </Box>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: "500px", bgcolor: "white" }}>
      <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
        <Autocomplete
          options={jomarStores}
          getOptionLabel={(option) => `${option.name}`}
          onChange={(event, newValue) => {
            if (newValue) {
              setSelectedStore(newValue);
              if (map) {
                map.panTo({ lat: newValue.lat, lng: newValue.lng });
                map.setZoom(14); // Zoom close to the selected store
              }
            } else {
              setSelectedStore(null);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Busca una sucursal"
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                }
              }}
            />
          )}
        />
      </Box>
      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%", minHeight: "450px" }}
      center={center}
      zoom={11}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
          { featureType: "poi.business", stylers: [{ visibility: "off" }] },
          { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        ],
      }}
    >
      <style>{globalMapStyles}</style>
      
      {jomarStores.map((store) => (
        <Marker
          key={store.id}
          position={{ lat: store.lat, lng: store.lng }}
          onClick={() => setSelectedStore(store)}
          icon={{
            url: require("../assets/joma/logojomar.png"),
            scaledSize: new window.google.maps.Size(36, 36),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(18, 18),
          }}
        />
      ))}

      {selectedStore && (
        <InfoWindow
          position={{ lat: selectedStore.lat, lng: selectedStore.lng }}
          onCloseClick={() => setSelectedStore(null)}
          options={{
            pixelOffset: new window.google.maps.Size(0, -20)
          }}
        >
          <Box sx={{ 
            minWidth: "200px", 
            maxWidth: "250px", 
            overflow: "hidden", 
            borderRadius: "8px" 
          }}>
            {/* Header Image */}
            <Box
              sx={{
                width: "100%",
                height: "80px",
                backgroundImage: `url(${bgBlock})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
              }}
            >
              {/* Overlay to match the store card look */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(30, 60, 140, 0.4)",
                  zIndex: 1,
                }}
              />
            </Box>

            {/* Content Container */}
            <Box sx={{ p: 1.5, textAlign: "center", bgcolor: "white" }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5, color: "#1a1a1a" }}>
                {selectedStore.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 0.5, fontSize: "0.8rem", lineHeight: 1.2 }}>
                {selectedStore.address}
              </Typography>
              <Typography variant="body2" sx={{ color: "#2e7d32", mb: 1.5, fontSize: "0.75rem", fontWeight: "bold" }}>
                {selectedStore.hours}
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                fullWidth
                sx={{ 
                  backgroundColor: "#e31837", // Red color based on their branding
                  color: "white",
                  fontWeight: "bold",
                  borderRadius: "20px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#c6112a",
                  }
                }}
                onClick={() => {
                  window.location.href = "https://pide.mennu.mx/jomar";
                }}
              >
                Ir a tienda
              </Button>
            </Box>
          </Box>
        </InfoWindow>
      )}
      </GoogleMap>
      </Box>
    </Box>
  );
};

export default React.memo(MapComponent);
